import { zodResolver } from "@hookform/resolvers/zod";
import { orderBy } from "firebase/firestore";
import React from "react";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaSave } from "react-icons/fa";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

import { CORVUS_ORGANIZATION_ID } from "../../api/api.jsx";
import {
    LocationDocument,
    useAppCollection,
    useAppDocument,
    useLocationDocument,
    useOrganizationDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import AppSettingsFormFields, {
    buildAppSettingsSchema,
    getActiveAppSettingsModules,
    getAppSettingsValues,
} from "../../components/AppSettings/AppSettingsFormFields.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton";
import Layout, { Title } from "../../components/Layout/Layout";
import urls from "../../urls.jsx";
import { pruneObject } from "../../utils/objectUtils.js";

export default function LocationAppSettingsPage() {
    const { organizationId, locationId, appId } = useParams();
    const [organization] = useOrganizationDocument(organizationId, {
        mode: "once",
    });
    const [corvusOrganization] = useOrganizationDocument(
        CORVUS_ORGANIZATION_ID,
        { mode: "once" },
    );
    const [location] = useLocationDocument(organizationId, locationId, {
        mode: "once",
    });
    const [appDocument] = useAppDocument(appId, { mode: "once" });
    const [apps] = useAppCollection([orderBy("name", "asc")]);
    const navigate = useNavigate();

    const defaultAppSettings = React.useMemo(
        () => appDocument?.settings || {},
        [appDocument],
    );

    const activeSettingsModules = React.useMemo(
        () => getActiveAppSettingsModules(appDocument, "location", apps),
        [appDocument, apps],
    );

    const settingsSchema = React.useMemo(
        () => buildAppSettingsSchema(activeSettingsModules, z),
        [activeSettingsModules],
    );

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(settingsSchema),
        defaultValues: {
            settings: defaultAppSettings,
        },
    });

    const [isLoading, setIsLoading] = React.useState(false);
    const cloudConnections = Object.entries(
        organization?.cloud_connection?.connections ?? {},
    ).map(([key, value]) => ({
        id: key,
        name: value,
    }));

    const corvusCloudConnections = Object.entries(
        corvusOrganization?.cloud_connection?.connections ?? {},
    ).map(([key, value]) => ({
        id: key,
        name: value,
    }));

    const cloudConnectionIds = new Set(cloudConnections.map((c) => c.id));

    const allCloudConnections = [
        ...cloudConnections,
        ...corvusCloudConnections.filter((c) => !cloudConnectionIds.has(c.id)),
    ];

    const locationApp = location?.apps?.find((a) => a?.id === appId);

    React.useEffect(() => {
        setIsLoading(true);

        reset({
            settings: locationApp
                ? getAppSettingsValues(activeSettingsModules, locationApp)
                : {},
        });

        setIsLoading(false);
    }, [locationApp, reset, activeSettingsModules]);

    const onSubmit = async ({ settings }) => {
        if (!locationApp) {
            throw new Error(`Location app ${appId} not found`);
        }

        // Replace top-level keys
        const updatedSettings = {
            ...(locationApp.settings || {}),
            ...settings,
        };

        locationApp.settings = pruneObject(updatedSettings) || {};

        await LocationDocument(organizationId, locationId).set(location);
        navigate(
            `${urls.locationApps(organizationId, locationId)}?appId=${appId}`,
        );
    };

    return (
        <Layout>
            <title>App Setting | MyCorvus</title>
            <Breadcrumbs
                path={breadcrumbPaths.locationAppSettings}
                organization={organization}
                location={location}
                app={appDocument ?? null}
            />
            <Card className="mb-3">
                <Title title={`${appDocument?.name || "App"} Settings`} />
                <Card.Body className="p-4 py-0">
                    {isLoading ? (
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    ) : (
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <AppSettingsFormFields
                                activeSettingsModules={activeSettingsModules}
                                control={control}
                                errors={errors}
                                setValue={setValue}
                                defaultAppSettings={defaultAppSettings}
                                extraContext={{
                                    cloudConnections: allCloudConnections,
                                }}
                            />

                            <Button
                                variant="success"
                                disabled={isSubmitting || !isDirty}
                                type="submit"
                                className="mb-3 mt-1"
                            >
                                <FaSave className="mb-1 me-1" />
                                {isSubmitting ? "Saving..." : "Save"}
                            </Button>
                            <CancelButton
                                className="ms-2 mb-3 mt-1"
                                to={`${urls.locationApps(
                                    organizationId,
                                    locationId,
                                )}?appId=${appId}`}
                            />
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </Layout>
    );
}
